import React, { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useTranslation } from 'react-i18next';
import { 
  MessageCircle, 
  Send, 
  Check, 
  CheckCheck, 
  Bot,
  User,
  TrendingUp
} from 'lucide-react';

interface Message {
  id: string;
  text: string;
  sender: 'user' | 'bot';
  timestamp: string;
  status?: 'sending' | 'sent' | 'delivered' | 'read';
  delay?: number;
}

// Function to get translated demo messages
const getDemoMessages = (t: any): Message[] => [
  // Expense Tracking Demo
  { id: '1', text: t('landing.whatsapp.messages.user1'), sender: 'user', timestamp: '10:30', status: 'read', delay: 500 },
  { id: '2', text: t('landing.whatsapp.messages.bot1'), sender: 'bot', timestamp: '10:30', delay: 1200 },
  
  { id: '3', text: t('landing.whatsapp.messages.user2'), sender: 'user', timestamp: '11:15', status: 'read', delay: 2000 },
  { id: '4', text: t('landing.whatsapp.messages.bot2'), sender: 'bot', timestamp: '11:15', delay: 3200 },
  
  // Monthly Summary Demo
  { id: '5', text: t('landing.whatsapp.messages.user3'), sender: 'user', timestamp: '18:45', status: 'read', delay: 4500 },
  { id: '6', text: t('landing.whatsapp.messages.bot3'), sender: 'bot', timestamp: '18:45', delay: 6000 },
  
  // Savings Plan Demo
  { id: '7', text: t('landing.whatsapp.messages.user4'), sender: 'user', timestamp: '19:30', status: 'read', delay: 7500 },
  { id: '8', text: t('landing.whatsapp.messages.bot4'), sender: 'bot', timestamp: '19:32', delay: 9000 }
];

export function WhatsAppDemo() {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'he';
  const [displayedMessages, setDisplayedMessages] = useState<Message[]>([]);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentMessageIndex, setCurrentMessageIndex] = useState(0);
  const [typingIndicator, setTypingIndicator] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout>();
  const messagesContainerRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    // Scroll only within the messages container, not the entire page
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
    }
  };

  useEffect(() => {
    // Add a small delay to ensure the message is rendered before scrolling
    const timeoutId = setTimeout(() => {
      scrollToBottom();
    }, 50);
    
    return () => clearTimeout(timeoutId);
  }, [displayedMessages, typingIndicator]);

  // Auto-start demo on component mount and language change
  useEffect(() => {
    const startDemo = () => {
      setTimeout(() => {
        playDemo();
      }, 500); // Start after 0.5 seconds
    };
    
    startDemo();
    
    // Clean up on unmount
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [i18n.language]); // Restart demo when language changes

  const playDemo = () => {
    setDisplayedMessages([]);
    setCurrentMessageIndex(0);
    setIsPlaying(true);
    setTypingIndicator(false);
    
    const messages = getDemoMessages(t);
    let currentIndex = 0;
    
    const addNextMessage = () => {
      if (currentIndex < messages.length) {
        const message = messages[currentIndex];
        
        // Show typing indicator for bot messages
        if (message.sender === 'bot') {
          setTypingIndicator(true);
          setTimeout(() => {
            setTypingIndicator(false);
            setDisplayedMessages(prev => [...prev, message as Message]);
            setCurrentMessageIndex(currentIndex + 1);
            currentIndex++;
            
            if (currentIndex < messages.length) {
              const nextMessage = messages[currentIndex];
              timeoutRef.current = setTimeout(addNextMessage, nextMessage.delay || 800);
            } else {
              // Loop the demo
              setTimeout(() => {
                playDemo();
              }, 1500);
            }
          }, 800); // Faster typing delay
        } else {
          // User messages appear immediately
          setDisplayedMessages(prev => [...prev, message as Message]);
          setCurrentMessageIndex(currentIndex + 1);
          currentIndex++;
          
          if (currentIndex < messages.length) {
            const nextMessage = messages[currentIndex];
            timeoutRef.current = setTimeout(addNextMessage, nextMessage.delay || 800);
          }
        }
      }
    };
    
    // Start with first message
    const firstMessage = messages[0];
    timeoutRef.current = setTimeout(addNextMessage, firstMessage.delay || 800);
  };



  const formatTimestamp = (timestamp: string) => {
    return timestamp;
  };

  const renderMessage = (message: Message) => {
    const isUser = message.sender === 'user';
    
    return (
      <div key={message.id} className={`flex ${isUser ? 'justify-end' : 'justify-start'} mb-4 animate-fadeIn`}>
        <div className={`max-w-[85%] ${isUser ? 'order-2' : 'order-1'}`}>
          {!isUser && (
            <div className="flex items-center mb-2 ml-2">
              <div className="w-6 h-6 bg-gradient-to-br from-green-400 to-green-600 rounded-full flex items-center justify-center mr-2">
                <Bot className="h-3 w-3 text-white" />
              </div>
              <span className="text-xs text-gray-600 dark:text-gray-400 font-medium">FINGENT AI</span>
            </div>
          )}
          
          <div className={`
            relative px-4 py-3 shadow-lg
            ${isUser 
              ? 'bg-gradient-to-br from-green-500 to-green-600 text-white rounded-3xl rounded-br-lg' 
              : 'bg-white dark:bg-gray-800 text-gray-900 dark:text-white rounded-3xl rounded-bl-lg border border-gray-100 dark:border-gray-700'
            }
          `}>
            <div className="whitespace-pre-wrap text-sm leading-relaxed">{message.text}</div>
            
            <div className={`flex items-center justify-end mt-2 text-xs ${
              isUser ? 'text-green-100' : 'text-gray-500 dark:text-gray-400'
            }`}>
              <span className="mr-2">{formatTimestamp(message.timestamp)}</span>
              {isUser && (
                <div className="flex">
                  {message.status === 'sent' && <Check className="h-3 w-3" />}
                  {message.status === 'delivered' && <CheckCheck className="h-3 w-3" />}
                  {message.status === 'read' && <CheckCheck className="h-3 w-3 text-blue-200" />}
                </div>
              )}
            </div>
            
            {/* Message tail */}
            <div className={`absolute bottom-0 ${
              isUser 
                ? 'right-0 w-4 h-4 bg-gradient-to-br from-green-500 to-green-600' 
                : 'left-0 w-4 h-4 bg-white dark:bg-gray-800 border-b border-l border-gray-100 dark:border-gray-700'
            } transform rotate-45 ${isUser ? 'translate-x-2' : '-translate-x-2'} translate-y-2`}></div>
          </div>
        </div>
      </div>
    );
  };

  const renderTypingIndicator = () => (
    <div className="flex justify-start mb-4 animate-fadeIn">
      <div className="max-w-[85%]">
        <div className="flex items-center mb-2 ml-2">
          <div className="w-6 h-6 bg-gradient-to-br from-green-400 to-green-600 rounded-full flex items-center justify-center mr-2">
            <Bot className="h-3 w-3 text-white" />
          </div>
                     <span className="text-xs text-gray-600 dark:text-gray-400 font-medium">FINGENT AI is typing...</span>
        </div>
        
        <div className="bg-white dark:bg-gray-800 rounded-3xl rounded-bl-lg border border-gray-100 dark:border-gray-700 px-4 py-3 shadow-lg relative">
          <div className="flex items-center space-x-1">
            <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
            <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
            <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
          </div>
          
          {/* Message tail */}
          <div className="absolute bottom-0 left-0 w-4 h-4 bg-white dark:bg-gray-800 border-b border-l border-gray-100 dark:border-gray-700 transform rotate-45 -translate-x-2 translate-y-2"></div>
        </div>
      </div>
    </div>
  );

  return (
    <section id="whatsapp-demo" className="py-20 bg-gradient-to-br from-green-50 to-emerald-50 dark:from-gray-900 dark:to-green-900/20">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <Badge variant="secondary" className="mb-4 bg-green-100 text-green-800 border-green-200">
            <MessageCircle className="h-4 w-4 mr-2" />
            {t('landing.whatsapp.badge')}
          </Badge>
          
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white sm:text-4xl mb-4">
            {t('landing.whatsapp.title')}
          </h2>
          
          <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
            {t('landing.whatsapp.description')}
          </p>
        </div>

        <div className="flex flex-col items-center">
          {/* WhatsApp Interface */}
          <div className="w-full max-w-md mx-auto mb-8">
            <div className="bg-gradient-to-b from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-800 rounded-[3rem] p-6 shadow-2xl">
              <div className="bg-white dark:bg-gray-900 rounded-[2.5rem] overflow-hidden shadow-xl">
                {/* WhatsApp Header */}
                <div className="bg-gradient-to-r from-green-500 to-green-600 text-white p-4 flex items-center">
                  <div className="w-12 h-12 bg-gradient-to-br from-green-600 to-green-700 rounded-full flex items-center justify-center mr-3 shadow-lg">
                    <Bot className="h-6 w-6" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg">FINGENT AI</h3>
                    <p className="text-xs text-green-100 flex items-center">
                      <span className="w-2 h-2 bg-green-300 rounded-full mr-1 animate-pulse inline-block"></span>
                      Online • AI Financial Assistant
                    </p>
                  </div>
                </div>

                {/* Messages Area */}
                <div 
                  ref={messagesContainerRef}
                  className="h-[500px] overflow-y-auto p-4 scroll-smooth whatsapp-messages-container" 
                  style={{
                    background: 'linear-gradient(135deg, #f0f8ff 0%, #e6f3ff 100%)',
                    backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.1'%3E%3Cpath d='m36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
                  }}
                >
                  {displayedMessages.length === 0 && !isPlaying ? (
                    <div className="text-center text-gray-500 mt-32">
                      <div className="w-16 h-16 bg-gradient-to-br from-green-400 to-green-600 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                        <Bot className="h-8 w-8 text-white" />
                      </div>
                      <p className="text-sm">Demo will start automatically...</p>
                    </div>
                  ) : (
                    <>
                      {displayedMessages.map(renderMessage)}
                      {typingIndicator && renderTypingIndicator()}
                    </>
                  )}
                </div>

                {/* Input Area */}
                <div className="p-4 bg-gray-50 dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700">
                  <div className="flex items-center space-x-3">
                    <div className="flex-1 bg-white dark:bg-gray-700 rounded-full px-4 py-3 text-sm text-gray-500 border border-gray-200 dark:border-gray-600 shadow-inner">
                      Type a message...
                    </div>
                    <Button size="sm" className="rounded-full w-12 h-12 p-0 bg-gradient-to-br from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 shadow-lg">
                      <Send className="h-5 w-5" />
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>


        </div>

        {/* Features Highlight */}
        <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <Card className="p-6 text-center">
            <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center mx-auto mb-4">
              <MessageCircle className="h-6 w-6 text-blue-600 dark:text-blue-400" />
            </div>
            <h3 className="font-semibold mb-2">Natural Language</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Just type naturally - no forms or complicated interfaces
            </p>
          </Card>

          <Card className="p-6 text-center">
            <div className="w-12 h-12 bg-green-100 dark:bg-green-900/30 rounded-lg flex items-center justify-center mx-auto mb-4">
              <Bot className="h-6 w-6 text-green-600 dark:text-green-400" />
            </div>
            <h3 className="font-semibold mb-2">Smart Categorization</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              AI automatically categorizes and organizes your expenses
            </p>
          </Card>

          <Card className="p-6 text-center">
            <div className="w-12 h-12 bg-purple-100 dark:bg-purple-900/30 rounded-lg flex items-center justify-center mx-auto mb-4">
              <TrendingUp className="h-6 w-6 text-purple-600 dark:text-purple-400" />
            </div>
            <h3 className="font-semibold mb-2">Instant Insights</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Get spending summaries and financial insights on demand
            </p>
          </Card>

          <Card className="p-6 text-center">
            <div className="w-12 h-12 bg-orange-100 dark:bg-orange-900/30 rounded-lg flex items-center justify-center mx-auto mb-4">
              <User className="h-6 w-6 text-orange-600 dark:text-orange-400" />
            </div>
            <h3 className="font-semibold mb-2">Personal Planning</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              AI creates personalized savings plans based on your data
            </p>
          </Card>
        </div>
      </div>
    </section>
  );
}

export default WhatsAppDemo; 